/**
 * Formulário de usuário (novo e edição) — Nexora Grid (frontend, US-1804).
 *
 * Criado em: 19/10/2026 · Última modificação: 02/11/2026 · Versão: 2.0.0
 *
 * Sem campo de senha (US-1803): quem é criado recebe o convite por e-mail e define a própria senha.
 * O administrador do cliente não vê o papel "Administrador" nem outra operadora (o backend recusa de qualquer forma).
 * v2.0 (US-4604, Sprint 46): campos em grupos com título (Identificação, Vínculos de acesso, Horário de acesso), validação
 *   ao sair do campo com a mensagem embaixo, rodapé fixo com "alterações não salvas" e aviso do navegador ao sair com
 *   alterações. Mesmos campos, mesmas regras e o mesmo corpo da API.
 */
import { useRef, useState, type FormEvent } from "react";
import type { Usuario } from "../api";
import { Campo, GrupoCampos, RodapeFormulario, useAvisoAoSair } from "../componentes/Tela";
import { DIAS, PAPEIS, type Janela } from "./tipos";
import { marcaAtual } from "../marca";
import "./admin-b.css";

export type DadosForm = {
  login: string; nome: string; email: string; perfil: "cliente" | "suporte" | "integrador"; empresa: string; papeis: string[];
  empresas_atribuidas: string; usarJanela: boolean; janela: Janela;
};

const JANELA_PADRAO: Janela = { dias: [0, 1, 2, 3, 4], inicio: "07:00", fim: "19:00" };
const LOGIN_VALIDO = /^[a-z0-9][a-z0-9._-]{2,59}$/;

export function dadosIniciais(ator: Usuario, empresa: string | null): DadosForm {
  return { login: "", nome: "", email: "", perfil: "cliente", empresa: ator.perfil === "integrador" ? "" : (empresa ?? ""),
    papeis: ["operador"], empresas_atribuidas: "", usarJanela: false, janela: JANELA_PADRAO };
}

type Props = {
  ator: Usuario; inicial: DadosForm; novo: boolean; enviando: boolean; textoBotao: string;
  aoEnviar: (dados: DadosForm) => void; aoCancelar?: () => void;
};

/** Mensagens de erro de cada campo (vazio = campo certo). */
function errosDe(d: DadosForm, novo: boolean): Partial<Record<keyof DadosForm, string>> {
  const e: Partial<Record<keyof DadosForm, string>> = {};
  if (novo && !LOGIN_VALIDO.test(d.login)) e.login = "Use de 3 a 60 caracteres: minúsculas, números, ponto, hífen ou sublinhado.";
  if (d.nome.trim().length < 3) e.nome = "Escreva o nome com pelo menos 3 letras.";
  if (novo && !d.email) e.email = "Informe o e-mail: o convite vai para ele.";
  else if (d.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email)) e.email = "E-mail incompleto (ex.: maria@operadora.com.br).";
  if (novo && d.perfil === "cliente" && !d.empresa) e.empresa = "Informe a operadora (ex.: empresa01).";
  if (d.perfil === "suporte" && !d.empresas_atribuidas.trim()) e.empresas_atribuidas = "Informe ao menos uma operadora.";
  return e;
}

export function FormUsuario({ ator, inicial, novo, enviando, textoBotao, aoEnviar, aoCancelar }: Props) {
  const [d, setD] = useState<DadosForm>(inicial);
  const [tocados, setTocados] = useState<Set<string>>(new Set());
  const form = useRef<HTMLFormElement>(null);
  const nexora = ator.perfil === "integrador";
  const podeCriarIntegrador = (ator.funcoes_treinamento ?? []).includes("gestor_sistema");
  const muda = <K extends keyof DadosForm>(chave: K, valor: DadosForm[K]) => setD((atual) => ({ ...atual, [chave]: valor }));
  const tocar = (chave: string) => setTocados((t) => (t.has(chave) ? t : new Set(t).add(chave)));
  const alternarPapel = (papel: string) => muda("papeis", d.papeis.includes(papel) ? d.papeis.filter((p) => p !== papel) : [...d.papeis, papel]);
  const alternarDia = (dia: number) => muda("janela", { ...d.janela, dias: d.janela.dias.includes(dia)
    ? d.janela.dias.filter((x) => x !== dia) : [...d.janela.dias, dia].sort() });
  const papeisDisponiveis = PAPEIS.filter(([p]) => nexora || p !== "administrador");
  const erros = errosDe(d, novo);
  const erro = (chave: keyof DadosForm) => (tocados.has(chave) ? erros[chave] : undefined);
  const bloqueadoIntegrador = novo && d.perfil === "integrador" && !podeCriarIntegrador;
  const valido = Object.keys(erros).length === 0 && !bloqueadoIntegrador;
  const sujo = JSON.stringify(d) !== JSON.stringify(inicial);
  useAvisoAoSair(sujo && !enviando);
  const enviar = (e: FormEvent) => {
    e.preventDefault();
    if (!valido) { setTocados(new Set(Object.keys(erros))); return; }
    aoEnviar(d);
  };

  return (
    <form ref={form} className="form-usuario-b tl-bloco" onSubmit={enviar}>
      <div className="form-b">
        <GrupoCampos titulo="Identificação">
          {novo && (
            <Campo id="u-login" rotulo="Login" erro={erro("login")} ajuda="minúsculas, números, ponto, hífen ou sublinhado">
              <input id="u-login" required pattern="[a-z0-9][a-z0-9._\-]{2,59}" value={d.login} onBlur={() => tocar("login")}
                onChange={(e) => muda("login", e.target.value.trim().toLowerCase())} placeholder="ex.: maria.silva" autoComplete="off" />
            </Campo>
          )}
          <Campo id="u-nome" rotulo="Nome" erro={erro("nome")}>
            <input id="u-nome" required minLength={3} maxLength={120} value={d.nome} onBlur={() => tocar("nome")} onChange={(e) => muda("nome", e.target.value)} />
          </Campo>
          <Campo id="u-email" rotulo="E-mail" erro={erro("email")} ajuda={novo ? "o convite para definir a senha vai para este e-mail" : undefined}>
            <input id="u-email" type="email" required={novo} value={d.email} onBlur={() => tocar("email")} onChange={(e) => muda("email", e.target.value.trim())} />
          </Campo>
          {novo && nexora && (
            <Campo id="u-perfil" rotulo="Perfil">
              <select id="u-perfil" value={d.perfil} onChange={(e) => muda("perfil", e.target.value as DadosForm["perfil"])}>
                <option value="cliente">Cliente (equipe da operadora)</option>
                <option value="suporte">Suporte {marcaAtual().empresa} (só consulta)</option>
                {podeCriarIntegrador && <option value="integrador">Integrador {marcaAtual().empresa} (equipe)</option>}
              </select>
            </Campo>
          )}
          {novo && d.perfil === "cliente" && (
            <Campo id="u-empresa" rotulo="Operadora" erro={erro("empresa")} ajuda={!nexora ? "a sua operadora" : undefined}>
              <input id="u-empresa" required readOnly={!nexora} value={d.empresa} onBlur={() => tocar("empresa")}
                onChange={(e) => muda("empresa", e.target.value.trim().toLowerCase())} placeholder="ex.: empresa01" />
            </Campo>
          )}
        </GrupoCampos>

        <GrupoCampos titulo="Vínculos de acesso">
          {d.perfil === "cliente" ? (
            <fieldset className="tl-campo largo grupo-papeis" style={{ border: 0, padding: 0, margin: 0 }}>
              <legend className="visualmente-oculto">Vínculos de acesso — papéis</legend>
              <span className="tl-ajuda nx-suave">Os papéis determinam as capacidades e os acessos disponíveis para este usuário.</span>
              <div className="opcoes">
                {papeisDisponiveis.map(([p, nome]) => (
                  <label key={p} className="opcao"><input type="checkbox" checked={d.papeis.includes(p)} onChange={() => alternarPapel(p)} /> {nome}</label>
                ))}
              </div>
            </fieldset>
          ) : d.perfil === "integrador" ? (
            <p className="tl-texto tl-campo largo grupo-papeis">A equipe {marcaAtual().empresa} enxerga todas as operadoras. As funções (gestor, validador, QA, implantação) são concedidas em Treinamento › Equipe e auditoria.</p>
          ) : (
            <Campo id="u-atribuidas" rotulo="Operadoras atribuídas" largo erro={erro("empresas_atribuidas")} ajuda="separadas por vírgula">
              <input id="u-atribuidas" required={d.perfil === "suporte"} value={d.empresas_atribuidas} onBlur={() => tocar("empresas_atribuidas")}
                onChange={(e) => muda("empresas_atribuidas", e.target.value)} placeholder="separadas por vírgula" />
            </Campo>
          )}
        </GrupoCampos>

        <GrupoCampos titulo="Horário de acesso">
          <div className="tl-campo largo grupo-janela">
            <label className="opcao"><input type="checkbox" checked={d.usarJanela} onChange={(e) => muda("usarJanela", e.target.checked)} /> Restringir dias e horário</label>
            {d.usarJanela && (
              <div className="janela-b">
                <div className="opcoes" role="group" aria-label="Dias da semana">
                  {DIAS.map((nome, i) => (
                    <label key={nome} className="opcao"><input type="checkbox" checked={d.janela.dias.includes(i)} onChange={() => alternarDia(i)} /> {nome}</label>
                  ))}
                </div>
                <label className="opcao">das <input type="time" aria-label="Início do horário" value={d.janela.inicio.slice(0, 5)}
                  onChange={(e) => muda("janela", { ...d.janela, inicio: e.target.value })} /></label>
                <label className="opcao">às <input type="time" aria-label="Fim do horário" value={d.janela.fim.slice(0, 5)}
                  onChange={(e) => muda("janela", { ...d.janela, fim: e.target.value })} /></label>
                <span className="tl-ajuda nx-suave">fim antes do início = plantão que passa da meia-noite</span>
              </div>
            )}
          </div>
        </GrupoCampos>

        <RodapeFormulario sujo={sujo || novo} valido={valido} salvando={enviando} rotuloSalvar={textoBotao} aoCancelar={aoCancelar}
          aoSalvar={() => form.current?.requestSubmit()} />
      </div>
    </form>
  );
}

/** Converte o formulário no corpo da API (`/api/v1/usuarios`). */
export function corpoDoForm(d: DadosForm, novo: boolean): Record<string, unknown> {
  const janela = d.usarJanela ? { dias: d.janela.dias, inicio: `${d.janela.inicio.slice(0, 5)}:00`, fim: `${d.janela.fim.slice(0, 5)}:00` } : null;
  const comum: Record<string, unknown> = { nome: d.nome, email: d.email || undefined };
  if (d.perfil === "cliente") comum.papeis = d.papeis;
  else if (d.perfil === "suporte") comum.empresas_atribuidas = d.empresas_atribuidas.split(",").map((e) => e.trim().toLowerCase()).filter(Boolean);
  if (novo) return { ...comum, login: d.login, perfil: d.perfil, ...(d.perfil === "cliente" ? { empresa: d.empresa } : {}),
    ...(janela ? { janela_acesso: janela } : {}) };
  return { ...comum, ...(janela ? { janela_acesso: janela } : { sem_janela: true }) };
}
